import { produce } from "immer";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../auth-context";
import { inputValueNow } from "../utils/date";
import { unflatten } from "../utils/objects";
import { roundTo } from "../utils/strings";
import ActionButton from "../widget/action-button";
import ButtonsGroup from "../widget/buttons-group";
import IODate from "../widget/io-date";
import IOHidden from "../widget/io-hidden";
import IOList from "../widget/io-list";
import IONumber from "../widget/io-number";
import IOPanelWithNavigation from "../widget/io-panel-with-navigation";
import Panel from "../widget/panel";
import TwoColumns from "../widget/two-columns";
import type {
  Bill,
  BillLine,
  Patient,
  Payment,
  Price,
  Prices
} from "./_objects-patient";
import IOBillLine from "./blocs/io-bill-line";
import { type RelatedElementGeneratorProps } from "./patient-related-element-generator";
import PaymentElement from "./payment-element";
import { getPriceLinesByCache } from "./price-cache";

export default function BillElement(
  props: { patient: Patient } & RelatedElementGeneratorProps<Bill>
): React.ReactNode {
  //
  // React context
  //

  const authenticationContext = useContext(AuthContext)!;

  //
  // Date & Price
  //

  const [date, setDate] = useState<string | undefined>(
    props.file.date ?? undefined
  );
  useEffect(() => {
    setDate(props.file?.date ?? undefined);
  }, [props.file.date]);

  const [prices, setPrices] = useState<Prices | null>(null);
  useEffect(() => {
    // Because it is async
    getPriceLinesByCache(date ?? null).then((calculatedPrice) => {
      if (calculatedPrice?.date_from != prices?.date_from) {
        setPrices(calculatedPrice);
      }
    });
  }, [date]);

  const getPriceByKey = (key: string): number => {
    if (!prices) {
      return 0;
    }
    return prices.prices.filter((price) => price.key == key)?.[0]?.price ?? 0;
  };

  const categories = Array.from(
    new Set(
      prices?.prices
        .map((price) => price.category)
        .filter((category) => category != null && category != "") ?? []
    )
  ).sort();

  //
  // Bill Lines
  //

  const [bill_lines, setBillLines] = useState<BillLine[]>(
    props.file.bill_lines ?? []
  );
  useEffect(() => {
    setBillLines(props.file.bill_lines ?? []);
  }, [props.file?.updated_at]);

  const updateBillLines = (value: BillLine) => {
    setBillLines(
      produce<BillLine[]>(bill_lines, (draft) =>
        draft
          .filter((bill_line) => bill_line.price_id != value.price_id)
          .concat([value])
      )
    );
  };

  //
  // Social Level
  //

  const [socialLevelParams, setSocialLevelParams] = useState({
    family_salary: props.file.social_level_family_salary,
    number_of_household_members:
      props.file.social_level_number_of_household_members
  });
  useEffect(() => {
    setSocialLevelParams({
      family_salary: props.file.social_level_family_salary,
      number_of_household_members:
        props.file.social_level_number_of_household_members
    });
  }, [props.file.updated_at]);

  const rationSalary: number = Math.ceil(
    socialLevelParams.family_salary /
      socialLevelParams.number_of_household_members
  );

  let socialLevel = 4;
  if (
    (socialLevelParams.family_salary ?? 0) > 0 &&
    (socialLevelParams.number_of_household_members ?? 0) > 0
  ) {
    if (prices && isFinite(rationSalary)) {
      switch (true) {
        case rationSalary <= getPriceByKey("social_level_threshold_1"):
          socialLevel = 0;
          break;
        case rationSalary <= getPriceByKey("social_level_threshold_2"):
          socialLevel = 1;
          break;
        case rationSalary <= getPriceByKey("social_level_threshold_3"):
          socialLevel = 2;
          break;
        case rationSalary <= getPriceByKey("social_level_threshold_4"):
          socialLevel = 3;
          break;
        default:
          socialLevel = 4;
          break;
      }
    }
  }

  const percentageAsked =
    getPriceByKey(`social_level_percentage_${socialLevel}`) ?? 1;

  //
  // Totals
  //

  const totalBy = (category: string): number =>
    bill_lines
      .filter(
        (line: BillLine) =>
          prices?.prices.find((price) => price.id == line.price_id)?.category ==
          category
      )
      .reduce(
        (acc, val: BillLine) =>
          acc +
          val.value *
            (prices?.prices.filter((price) => price.id == val.price_id)[0]
              ?.price ?? 0),
        0
      );

  const totalReal = categories
    .map((category) => totalBy(category))
    .reduce((acc, val) => acc + val, 0);

  const totalAsked = Math.round((totalReal * percentageAsked) / 100);

  const totalPayments = roundTo(
    Object.values(props.file.payment ?? {})
      .map((p) => p.amount)
      .reduce((acc, v) => acc + v, 0)
  );

  //
  // Saving
  //
  const prepareData = (formData: FormData): Record<string, unknown> => {
    const data = Object.fromEntries(formData.entries());
    const structured = unflatten<Bill>(data);

    structured.bill_lines =
      structured.bill_lines?.filter((line) => line.value > 0) ?? [];

    return structured as unknown as Record<string, unknown>;
  };

  //
  // Payments
  //

  const doAddPayment = () => {
    // ???
    props.onUpdated(
      produce<Bill>(props.file, (draft) => {
        if (!draft.payment) {
          draft.payment = {};
        }
        draft.payment["add"] = {
          bill_id: props.file.id!,
          amount: 0,
          date: inputValueNow()
        };
      })
    );
  };

  /** *************************
   *
   * Render
   *
   */
  return (
    <IOPanelWithNavigation<Bill>
      {...props}
      key={`bill.${props.file.id}`}
      type="bill"
      apiRootUrl={`/api/patient/${props.patient.id}/bill`}
      restrictedForWrite="write"
      restrictedForUnlock="manage_archives"
      isProtected={Object.values(props.file.payment ?? {}).length > 0}
      canBeLocked={true}
      prepareData={prepareData}
      onUpdated={(file: Bill) =>
        props.onUpdated(
          produce(file, (draft) => {
            draft.payment = props.file.payment;
            return draft;
          })
        )
      }
      basePath={`${props.parentPath}/bill`}
      header={
        <>
          <span>total: {totalReal}</span>
          <span>paid: {totalPayments}</span>
        </>
      }
      footer={
        !props.edit &&
        props.file.id &&
        prices && (
          <Panel
            fixed
            label="Payments"
            testid={`bill.${props.file.id}.payments`}
          >
            <ButtonsGroup>
              <ActionButton
                style="Add"
                restrictedTo="write"
                onOk={doAddPayment}
              />
            </ButtonsGroup>
            {Object.values(props.file.payment ?? {}).length == 0 ? (
              <div>No payment received</div>
            ) : (
              Object.values(props.file.payment ?? {}).map((payment) => (
                <PaymentElement
                  key={`payment.${payment.id ?? "add"}`}
                  file={payment}
                  edit={!payment.id}
                  closed={!!payment.id}
                  patient_id={props.file.patient_id!}
                  onCreated={(subFile: Payment) =>
                    props.onUpdated(
                      produce(props.file, (draft) => {
                        if (!draft.payment) {
                          draft.payment = {};
                        }
                        delete draft.payment["add"];
                        draft.payment[subFile.id!] = subFile;
                      })
                    )
                  }
                  onUpdated={(subFile: Payment) =>
                    props.onUpdated(
                      produce(props.file, (draft) => {
                        if (!draft.payment) {
                          draft.payment = {};
                        }
                        draft.payment[subFile.id!] = subFile;
                      })
                    )
                  }
                  onDeleted={(subFile: Payment) =>
                    props.onUpdated(
                      produce(props.file, (draft) => {
                        if (draft.payment) {
                          delete draft.payment[subFile.id!];
                          delete draft.payment["add"];
                        }
                      })
                    )
                  }
                />
              ))
            )}
          </Panel>
        )
      }
    >
      <div className="debug">
        {prices?.date_from} ⇒ {prices?.date_to} (#{prices?.prices.length} items)
      </div>
      <TwoColumns>
        <Panel fixed label="Information">
          <input
            type="hidden"
            name="patient_id"
            defaultValue={props.patient.id}
          />
          <IODate
            name="date"
            value={date}
            onChange={(value) => setDate(value)}
          />
          {prices && (
            <>
              <IOList
                name="examiner"
                value={props.file.examiner}
                list={authenticationContext!.getList("Examiners")}
              />
              <IOList
                name="center"
                value={props.file.center}
                list={authenticationContext!.getList("Centers")}
              />{" "}
              <IONumber
                name="social_level_family_salary"
                label="Family Salary"
                value={socialLevelParams.family_salary}
                onChange={(value) =>
                  setSocialLevelParams({
                    ...socialLevelParams,
                    family_salary: value
                  })
                }
              />
              <IONumber
                name="social_level_number_of_household_members"
                label="Number of Household Members"
                value={socialLevelParams.number_of_household_members}
                htmlProps={{ max: 10 }}
                onChange={(value) =>
                  setSocialLevelParams({
                    ...socialLevelParams,
                    number_of_household_members: value
                  })
                }
              />
              <IOHidden name="social_level" value={socialLevel} />
              <IOHidden
                name="social_level_percentage"
                label="Percentage"
                value={percentageAsked}
              />
            </>
          )}
        </Panel>
        {prices && (
          <Panel fixed label="Totals">
            {categories.map((category) => (
              <IOHidden
                key={category}
                name={`total_${category}`}
                value={totalBy(category)}
              />
            ))}
            <IOHidden
              name="total_real"
              label="Raw Calculated Total"
              value={totalReal}
            />
            <IOHidden
              name="total_asked"
              label="Price asked"
              value={totalAsked}
            />
            <IOHidden
              /* Force simple output */
              mode="output"
              label="Payments Received (see below)"
              value={totalPayments}
            />
          </Panel>
        )}
      </TwoColumns>
      {categories.map(
        (category, indexCategory) =>
          (totalBy(category) > 0 || props.edit) && (
            <Panel
              key={category}
              fixed
              label={category}
              header={<span>{totalBy(category)} ৳</span>}
            >
              {prices?.prices
                .filter((price) => price.category == category)
                .sort((a, b) => {
                  if (a.key === "Other") return 1; // Move 'a' to the end
                  if (b.key === "Other") return -1; // Move 'b' to the end

                  return a.key.localeCompare(b.key, "en", {
                    numeric: true
                  });
                })
                .map((price: Price, index: number) => (
                  <IOBillLine
                    index={indexCategory * 1000 + index}
                    value={
                      bill_lines.find(
                        (bLine: BillLine) => bLine.price_id == price.id
                      ) ?? ({ value: 0, price_id: price.id } as BillLine)
                    }
                    price={price}
                    key={price.key}
                    onChange={(bl) => updateBillLines(bl)}
                  />
                ))}
            </Panel>
          )
      )}
    </IOPanelWithNavigation>
  );
}
